import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Force clear any existing tokens on initialization
  const [token, setToken] = useState(() => {
    console.log('AUTH: Force clearing all tokens on init');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    return null;
  });

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('AUTH: Initializing - forcing clean state');
      // Force clean state - no token verification on init
      setUser(null);
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      console.log('AUTH: Starting login process...');
      const response = await authAPI.login(credentials);
      console.log('AUTH: Login API response:', response.data);

      const { token: newToken, user: userData } = response.data;
      console.log('AUTH: Extracted token and user:', { token: !!newToken, user: userData });

      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('user_data', JSON.stringify(userData));
      console.log('AUTH: Saved to localStorage');

      setToken(newToken);
      setUser(userData);
      console.log('AUTH: Updated state');

      // Store in window for debugging
      window.debugAuth = { token: newToken, user: userData };
      console.log('AUTH: Debug data stored in window.debugAuth');

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token: newToken, user: newUser } = response.data;

      localStorage.setItem('auth_token', newToken);
      localStorage.setItem('user_data', JSON.stringify(newUser));

      setToken(newToken);
      setUser(newUser);

      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Don't fail logout if API call fails - still clear local state
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      setToken(null);
      setUser(null);
    }
  };

  const updateUserProfile = (updatedProfile) => {
    const updatedUser = {
      ...user,
      profile: { ...user.profile, ...updatedProfile }
    };
    setUser(updatedUser);
    localStorage.setItem('user_data', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isCandidate = () => hasRole('candidate');
  const isClient = () => hasRole('client');
  const isAdmin = () => hasRole('admin');

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUserProfile,
    isAuthenticated,
    hasRole,
    isCandidate,
    isClient,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};